import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Ticket, Bot, Server, Clock, CheckCircle, XCircle, AlertTriangle,
  Loader2, RefreshCw, ChevronDown, ChevronUp, Play, RotateCcw,
  Zap, Users, Filter, Copy, ExternalLink, Info, FileCode, BarChart3, Wrench, Paperclip
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ConnectedSystems } from "@/components/ConnectedSystems";
import { HelpdeskAnalytics } from "@/components/HelpdeskAnalytics";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  open:        { label: "Open",        color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",   icon: Clock },
  triaging:    { label: "AI Triaging", color: "bg-purple-500/10 text-purple-400 border-purple-500/20", icon: Bot },
  in_progress: { label: "In Progress", color: "bg-blue-500/10 text-blue-400 border-blue-500/20",  icon: Loader2 },
  resolved:    { label: "Resolved",    color: "bg-green-500/10 text-green-400 border-green-500/20", icon: CheckCircle },
  failed:      { label: "Failed",      color: "bg-red-500/10 text-red-400 border-red-500/20",     icon: XCircle },
  closed:      { label: "Closed",      color: "bg-zinc-700/10 text-zinc-500 border-zinc-700/20",  icon: CheckCircle },
};

const PRIORITY_CFG = {
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high:     "bg-orange-500/20 text-orange-400 border-orange-500/30",
  medium:   "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low:      "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
};

const CATEGORY_ICONS = {
  odoo: "🔷", windows: "🪟", linux_server: "🐧",
  network: "🌐", email: "✉️", printer: "🖨️", other: "⚙️"
};

const TYPE_CFG = {
  bug:             "bg-red-500/10 text-red-300 border-red-500/20",
  incident:        "bg-orange-500/10 text-orange-300 border-orange-500/20",
  feature_request: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  question:        "bg-cyan-500/10 text-cyan-300 border-cyan-500/20",
  feedback:        "bg-purple-500/10 text-purple-300 border-purple-500/20",
};

function AttachmentGallery({ ticket }) {
  const [urls, setUrls] = useState({});
  const [preview, setPreview] = useState(null);
  const atts = ticket.attachments || [];

  useEffect(() => {
    if (!atts.length) return;
    let alive = true;
    const created = [];
    atts.forEach(async (a) => {
      try {
        const res = await axios.get(`${API}/it-tickets/${ticket.id}/attachments/${a.id}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        created.push(url);
        if (alive) setUrls(prev => ({ ...prev, [a.id]: url }));
      } catch {}
    });
    return () => { alive = false; created.forEach(u => URL.revokeObjectURL(u)); };
  }, [ticket.id]);

  if (!atts.length) return null;
  return (
    <div className="bg-white/5 rounded-lg p-3" data-testid="ticket-attachments-section">
      <p className="text-[10px] text-zinc-500 uppercase mb-2 flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> Attachments ({atts.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {atts.map((a, i) => (
          <button key={a.id} onClick={() => urls[a.id] && setPreview({ url: urls[a.id], name: a.filename })}
            className="group relative border border-white/10 rounded-lg overflow-hidden hover:border-blue-500/40 transition-colors"
            data-testid={`attachment-thumb-${i}`}>
            {urls[a.id] ? (
              <img src={urls[a.id]} alt={a.filename} className="h-24 w-36 object-cover" />
            ) : (
              <div className="h-24 w-36 flex items-center justify-center bg-black/40">
                <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
              </div>
            )}
            <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-zinc-300 px-1 py-0.5 truncate">{a.filename}</span>
          </button>
        ))}
      </div>
      {preview && (
        <Dialog open onOpenChange={() => setPreview(null)}>
          <DialogContent aria-describedby={undefined}
            className="bg-[#0c0c0e] border-white/10 text-white max-w-4xl"
            data-testid="attachment-preview-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm font-mono flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-blue-400" /> {preview.name}
              </DialogTitle>
            </DialogHeader>
            <img src={preview.url} alt={preview.name} className="max-h-[75vh] w-auto mx-auto rounded" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function DraftReplyPanel({ ticket, onRefresh }) {
  const [text, setText] = useState(ticket.ai_draft_reply || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setText(ticket.ai_draft_reply || ""); }, [ticket.ai_draft_reply]);

  if (ticket.reply_status === "approved") {
    return (
      <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-3" data-testid="approved-reply-section">
        <p className="text-xs text-green-400 uppercase mb-1 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Approved Reply
          <span className="text-zinc-500 normal-case ml-auto">by {ticket.reply_approved_by}</span>
        </p>
        <p className="text-sm text-zinc-200 whitespace-pre-line">{ticket.final_reply}</p>
      </div>
    );
  }

  if (!ticket.ai_draft_reply && ticket.reply_status !== "draft") return null;

  const approve = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/it-tickets/${ticket.id}/reply/approve`, { reply_text: text });
      toast.success(res.data.delivered_to_source ? "Reply approved & delivered to source app" : "Reply approved");
      if (res.data.needs_fix_ticket) {
        toast.warning("This is a bug — no fix ticket generated yet. Scroll up to generate one so it reaches the dev chat.", { duration: 7000 });
      }
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve reply"); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/it-tickets/${ticket.id}/reply/regenerate`);
      toast.info("Regenerating draft reply...");
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        const res = await axios.get(`${API}/it-tickets/${ticket.id}`);
        if (res.data.ai_draft_reply !== ticket.ai_draft_reply || tries >= 6) {
          clearInterval(iv);
          setBusy(false);
          onRefresh(res.data);
        }
      }, 2500);
    } catch { toast.error("Failed to regenerate"); setBusy(false); }
  };

  return (
    <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3 space-y-2" data-testid="draft-reply-section">
      <p className="text-xs text-blue-400 uppercase flex items-center gap-1">
        <Bot className="w-3 h-3" /> AI Draft Reply to User
        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 normal-case">
          Pending your review
        </span>
      </p>
      <Textarea value={text} onChange={e => setText(e.target.value)}
        className="bg-black/50 border-white/10 text-white resize-none text-sm min-h-[110px]"
        data-testid="draft-reply-textarea" />
      <div className="flex gap-2">
        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={approve} disabled={busy || !text.trim()}
          data-testid="approve-reply-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
          Approve & Send
        </Button>
        <Button size="sm" variant="outline" className="border-purple-500/20 text-purple-400 hover:bg-purple-500/10"
          onClick={regenerate} disabled={busy} data-testid="regenerate-reply-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Regenerate
        </Button>
      </div>
    </div>
  );
}

function StepRow({ step }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/5 rounded overflow-hidden">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/8 text-left text-xs">
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
          step.exit_code === 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
          {step.step}
        </span>
        <code className="text-blue-300 flex-1 truncate font-mono">{step.command}</code>
        <span className={`text-[10px] px-1 rounded font-mono ${step.exit_code === 0 ? "text-green-400" : "text-red-400"}`}>
          exit {step.exit_code}
        </span>
        {open ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
      </button>
      {open && (step.stdout || step.stderr) && (
        <div className="bg-black/60 p-2">
          {step.stdout && <pre className="text-xs text-green-300 font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">{step.stdout}</pre>}
          {step.stderr && <pre className="text-xs text-red-400 font-mono whitespace-pre-wrap max-h-24 overflow-y-auto">{step.stderr}</pre>}
        </div>
      )}
    </div>
  );
}

function DiagnosisPanel({ ticket, onStart }) {
  const running = ticket.diagnosis_status === "running";
  if (!ticket.diagnosis_status) {
    if (!ticket.server_id || ["resolved", "closed"].includes(ticket.status)) return null;
    return (
      <Button size="sm" variant="outline" className="border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/10"
        onClick={onStart} data-testid="run-diagnosis-btn">
        <Zap className="w-3.5 h-3.5 mr-1" /> Run AI Diagnosis (read-only)
      </Button>
    );
  }
  return (
    <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3 space-y-2" data-testid="diagnosis-section">
      <p className="text-xs text-cyan-400 uppercase flex items-center gap-1">
        <Bot className="w-3 h-3" /> AI Diagnosis (read-only)
        {running && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
        {ticket.diagnosis_status === "failed" && <span className="text-red-400 normal-case ml-1">failed</span>}
      </p>
      {ticket.diagnosis_report && (
        <p className="text-sm text-zinc-200 whitespace-pre-line">{ticket.diagnosis_report}</p>
      )}
      {ticket.diagnosis_log?.length > 0 && (
        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
          {ticket.diagnosis_log.map((step, i) => <StepRow key={i} step={step} />)}
        </div>
      )}
      {!running && (
        <Button size="sm" variant="outline" className="border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/10 h-7 text-xs"
          onClick={onStart} data-testid="rerun-diagnosis-btn">
          <RotateCcw className="w-3 h-3 mr-1" /> Re-run
        </Button>
      )}
    </div>
  );
}

function FixTicketPanel({ ticket, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/it-tickets/${ticket.id}/fix-ticket`);
      toast.success("Fix ticket generated — copy & paste it into that project's Emergent chat");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate fix ticket"); }
    finally { setBusy(false); }
  };

  const copyIt = () => {
    navigator.clipboard.writeText(ticket.fix_ticket_text);
    toast.success("Fix ticket copied — paste it into the project's Emergent chat");
  };

  if (!ticket.fix_ticket_text) {
    return (
      <Button size="sm" variant="outline" className="border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10"
        onClick={generate} disabled={busy} data-testid="generate-fix-ticket-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileCode className="w-3.5 h-3.5 mr-1" />}
        Generate Fix Ticket (for Emergent chat)
      </Button>
    );
  }
  return (
    <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3 space-y-2" data-testid="fix-ticket-section">
      <p className="text-xs text-emerald-400 uppercase flex items-center gap-1">
        <FileCode className="w-3 h-3" /> Fix Ticket — paste into the {ticket.system_name || "project"}'s Emergent chat
        <span className="text-zinc-600 normal-case ml-auto">{ticket.fix_ticket_generated_at && new Date(ticket.fix_ticket_generated_at).toLocaleString()}</span>
      </p>
      <pre className="text-xs text-zinc-200 whitespace-pre-wrap font-mono bg-black/40 rounded p-2.5 max-h-64 overflow-y-auto">{ticket.fix_ticket_text}</pre>
      <div className="flex gap-2">
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={copyIt} data-testid="copy-fix-ticket-btn">
          <Copy className="w-3.5 h-3.5 mr-1" /> Copy to Clipboard
        </Button>
        <Button size="sm" variant="outline" className="border-white/10 text-zinc-400 h-8" onClick={generate} disabled={busy}
          data-testid="regenerate-fix-ticket-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />} Regenerate
        </Button>
      </div>
    </div>
  );
}

const RISK_CFG = {
  low: "bg-green-500/10 text-green-300 border-green-500/20",
  medium: "bg-yellow-500/10 text-yellow-300 border-yellow-500/20",
  high: "bg-orange-500/10 text-orange-300 border-orange-500/20",
  critical: "bg-red-500/10 text-red-300 border-red-500/20",
};

const FIX_LOG_ICON = { snapshot: "📸", backup: "💾", step: "▶️", verify: "✅", rollback: "↩️", error: "⚠️" };

function FixPlanPanel({ ticket, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const plan = ticket.fix_plan;
  const status = ticket.fix_plan_status;

  const propose = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/it-tickets/${ticket.id}/propose-fix`);
      toast.success("Fix plan proposed — review each command before approving");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to propose fix"); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    if (!window.confirm(`Approve and EXECUTE this ${plan.risk_level}-risk fix on the server?\n\nA snapshot (if high/critical) and file backups will be taken first. Failed steps auto-rollback.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/it-tickets/${ticket.id}/fix-plan/approve`);
      toast.success("Fix approved — executing now");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/it-tickets/${ticket.id}/fix-plan/reject`);
      toast.info("Fix plan rejected");
      onRefresh();
    } catch { toast.error("Failed to reject"); }
    finally { setBusy(false); }
  };

  if (!status || status === "rejected") {
    if (!ticket.server_id || ["resolved", "closed"].includes(ticket.status)) return null;
    return (
      <Button size="sm" variant="outline" className="border-amber-500/20 text-amber-400 hover:bg-amber-500/10"
        onClick={propose} disabled={busy} data-testid="propose-fix-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
        Propose Auto-Fix (needs your approval)
      </Button>
    );
  }

  const executing = status === "executing";
  const borderColor = status === "executed" ? "border-green-500/20 bg-green-500/5"
    : status === "failed" ? "border-red-500/20 bg-red-500/5" : "border-amber-500/10 bg-amber-500/5";

  return (
    <div className={`rounded-lg p-3 space-y-2 border ${borderColor}`} data-testid="fix-plan-section">
      <div className="flex items-center gap-2">
        <p className="text-xs uppercase flex items-center gap-1 text-amber-400">
          <Wrench className="w-3 h-3" /> Approval-Gated Auto-Fix
        </p>
        {plan?.risk_level && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${RISK_CFG[plan.risk_level]}`}>
            {plan.risk_level} risk
          </span>
        )}
        <span className="ml-auto text-[10px] text-zinc-500">{status}{executing && <Loader2 className="w-3 h-3 animate-spin inline ml-1" />}</span>
      </div>

      {plan?.summary && <p className="text-sm text-zinc-200">{plan.summary}</p>}
      {plan?.from_runbook && (
        <p className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="proven-fix-badge">
          <CheckCircle className="w-3 h-3" /> Proven fix — successfully applied {plan.from_runbook.success_count}× before (first from {plan.from_runbook.source_ticket})
        </p>
      )}
      {plan?._blocked && <p className="text-xs text-red-400">⛔ {plan._blocked}</p>}

      {plan?.steps?.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] text-zinc-500 uppercase">Commands ({plan.steps.length})</p>
          {plan.steps.map((s, i) => (
            <div key={i} className="bg-black/40 rounded px-2 py-1.5">
              <code className="text-xs text-cyan-300 font-mono break-all">{s.command}</code>
              {s.description && <p className="text-[11px] text-zinc-500 mt-0.5">{s.description}</p>}
            </div>
          ))}
        </div>
      )}
      {plan?.backup_paths?.length > 0 && (
        <p className="text-[11px] text-zinc-500">💾 Backs up: {plan.backup_paths.join(", ")}</p>
      )}
      {plan?.rollback_commands?.length > 0 && (
        <p className="text-[11px] text-zinc-500">↩️ Rollback ready ({plan.rollback_commands.length} cmds)</p>
      )}

      {status === "proposed" && !plan?._blocked && (
        <div className="flex gap-2 pt-1">
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={approve} disabled={busy} data-testid="approve-fix-btn">
            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve & Execute
          </Button>
          <Button size="sm" variant="outline" className="border-white/10 text-zinc-400" onClick={reject} disabled={busy} data-testid="reject-fix-btn">
            Reject
          </Button>
          <Button size="sm" variant="outline" className="border-white/10 text-zinc-400" onClick={propose} disabled={busy}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-plan
          </Button>
        </div>
      )}

      {ticket.fix_execution_log?.length > 0 && (
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1 border-t border-white/5 pt-2">
          <p className="text-[10px] text-zinc-500 uppercase">Execution Log</p>
          {ticket.fix_execution_log.map((l, i) => (
            <div key={i} className="bg-black/40 rounded px-2 py-1">
              <p className="text-xs text-zinc-300">
                <span className="mr-1">{FIX_LOG_ICON[l.kind] || "•"}</span>
                <code className="font-mono break-all">{l.command}</code>
                {l.exit_code != null && <span className={l.exit_code === 0 ? "text-green-400 ml-1" : "text-red-400 ml-1"}> [exit {l.exit_code}]</span>}
              </p>
              {(l.stdout || l.stderr || l.note) && (
                <pre className="text-[10px] text-zinc-500 whitespace-pre-wrap mt-0.5">{(l.stdout || l.stderr || l.note).slice(0, 300)}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TicketModal({ ticket: initialTicket, onClose, onResolve, onRetriage }) {
  const [ticket, setTicket] = useState(initialTicket);
  const [resolveNote, setResolveNote] = useState("");
  const [showResolve, setShowResolve] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const active = ["triaging", "in_progress"].includes(ticket.status) || ticket.diagnosis_status === "running" || ticket.fix_plan_status === "executing";
    if (active) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/it-tickets/${ticket.id}`);
          setTicket(res.data);
          if (!["triaging", "in_progress"].includes(res.data.status) && res.data.diagnosis_status !== "running" && res.data.fix_plan_status !== "executing") {
            clearInterval(pollRef.current);
          }
        } catch {}
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [ticket.id, ticket.status, ticket.diagnosis_status, ticket.fix_plan_status]);

  const startDiagnosis = async () => {
    try {
      await axios.post(`${API}/it-tickets/${ticket.id}/diagnose`);
      toast.info("AI diagnosis started (read-only)...");
      const res = await axios.get(`${API}/it-tickets/${ticket.id}`);
      setTicket(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start diagnosis"); }
  };

  const cfg = STATUS_CFG[ticket.status] || STATUS_CFG.open;
  const StatusIcon = cfg.icon;
  const isLive = ["triaging", "in_progress"].includes(ticket.status);
  const agentTask = ticket.agent_task;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="bg-[#0c0c0e] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2">
            <Ticket className="w-5 h-5 text-blue-400" />
            {ticket.ticket_number} — {ticket.triage_notes || ticket.description.slice(0, 60)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status + Meta */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border font-medium ${cfg.color}`}>
              <StatusIcon className={`w-3.5 h-3.5 ${isLive ? "animate-spin" : ""}`} />
              {cfg.label}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs border font-medium ${PRIORITY_CFG[ticket.priority] || PRIORITY_CFG.medium}`}>
              {ticket.priority?.toUpperCase()}
            </span>
            {ticket.ticket_type && (
              <span className={`px-2 py-0.5 rounded-full text-xs border ${TYPE_CFG[ticket.ticket_type] || TYPE_CFG.incident}`}>
                {ticket.ticket_type.replace("_", " ")}
              </span>
            )}
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              {CATEGORY_ICONS[ticket.category]} {ticket.category}
            </span>
            <span className="text-xs text-zinc-600 ml-auto">{new Date(ticket.created_at).toLocaleString()}</span>
          </div>

          {/* User info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/5 rounded-lg p-2.5">
              <p className="text-[10px] text-zinc-500 uppercase mb-1">Submitted By</p>
              <p className="text-sm text-white font-medium">{ticket.name}</p>
              <p className="text-xs text-zinc-400">{ticket.department}</p>
              {ticket.submitter_email && <p className="text-xs text-zinc-500">{ticket.submitter_email}</p>}
            </div>
            <div className="bg-white/5 rounded-lg p-2.5">
              <p className="text-[10px] text-zinc-500 uppercase mb-1">Source</p>
              <p className="text-sm text-white capitalize">{ticket.system_name || ticket.source}</p>
              {ticket.erp_ticket_id && <p className="text-xs text-zinc-400">ERP: {ticket.erp_ticket_id}</p>}
            </div>
          </div>

          {/* Description */}
          <div className="bg-white/5 rounded-lg p-3">
            <p className="text-[10px] text-zinc-500 uppercase mb-1">Issue Description</p>
            <p className="text-sm text-zinc-200">{ticket.description}</p>
          </div>

          {/* Attachments */}
          <AttachmentGallery ticket={ticket} />

          {/* AI Triage */}
          {ticket.triage_notes && (
            <div className="bg-purple-500/5 border border-purple-500/10 rounded-lg p-3">
              <p className="text-xs text-purple-400 uppercase mb-1 flex items-center gap-1"><Bot className="w-3 h-3" /> AI Analysis</p>
              <p className="text-sm text-zinc-200">{ticket.triage_notes}</p>
            </div>
          )}

          {/* Agent Task */}
          {agentTask && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-400 uppercase flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-yellow-400" /> AI Execution ({agentTask.current_step || 0} steps)
                {isLive && <Loader2 className="w-3 h-3 animate-spin text-blue-400 ml-1" />}
              </p>
              {agentTask.execution_log?.length > 0 && (
                <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                  {agentTask.execution_log.map((step, i) => <StepRow key={i} step={step} />)}
                </div>
              )}
            </div>
          )}

          {/* AI Diagnosis */}
          <DiagnosisPanel ticket={ticket} onStart={startDiagnosis} />

          {/* Approval-gated Auto-Fix */}
          <FixPlanPanel ticket={ticket} onRefresh={async () => {
            try {
              const res = await axios.get(`${API}/it-tickets/${ticket.id}`);
              setTicket(res.data);
            } catch {}
          }} />

          {/* Fix Ticket */}
          <FixTicketPanel ticket={ticket} onRefresh={async () => {
            try {
              const res = await axios.get(`${API}/it-tickets/${ticket.id}`);
              setTicket(res.data);
            } catch {}
          }} />

          {/* AI Draft Reply */}
          <DraftReplyPanel ticket={ticket} onRefresh={async (fresh) => {
            if (fresh) { setTicket(fresh); return; }
            try {
              const res = await axios.get(`${API}/it-tickets/${ticket.id}`);
              setTicket(res.data);
            } catch {}
          }} />

          {/* Final Report */}
          {ticket.resolution_summary && (
            <div className={`rounded-lg p-3 border ${
              ticket.status === "resolved" ? "bg-green-500/5 border-green-500/10" : "bg-red-500/5 border-red-500/10"
            }`}>
              <p className={`text-xs uppercase mb-1 ${ticket.status === "resolved" ? "text-green-400" : "text-red-400"}`}>
                {ticket.status === "resolved" ? "Resolution Summary" : "Failure Summary"}
              </p>
              <p className="text-sm text-zinc-200 whitespace-pre-line">{ticket.resolution_summary}</p>
            </div>
          )}

          {/* Manual Resolve */}
          {!["resolved", "closed"].includes(ticket.status) && (
            <div className="space-y-2 pt-1 border-t border-white/5">
              {showResolve ? (
                <div className="space-y-2">
                  <Textarea value={resolveNote} onChange={e => setResolveNote(e.target.value)}
                    placeholder="Resolution summary..."
                    className="bg-black/50 border-white/10 text-white resize-none h-20 text-xs" />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700"
                      onClick={() => onResolve(ticket.id, resolveNote)}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Mark Resolved
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/10"
                      onClick={() => setShowResolve(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    className="border-green-500/20 text-green-400 hover:bg-green-500/10"
                    onClick={() => setShowResolve(true)}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Mark Resolved
                  </Button>
                  {ticket.status !== "in_progress" && (
                    <Button size="sm" variant="outline"
                      className="border-purple-500/20 text-purple-400 hover:bg-purple-500/10"
                      onClick={() => onRetriage(ticket.id)}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-triage
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ITTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("tickets");
  const pollRef = useRef(null);

  const fetch = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/it-tickets`),
        axios.get(`${API}/it-tickets/stats`)
      ]);
      setTickets(tRes.data);
      setStats(sRes.data);
    } catch { if (!silent) toast.error("Failed to load tickets"); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    const hasActive = tickets.some(t => ["triaging","in_progress"].includes(t.status));
    if (hasActive) {
      pollRef.current = setInterval(() => fetch(true), 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [tickets, fetch]);

  const handleResolve = async (id, summary) => {
    try {
      await axios.post(`${API}/it-tickets/${id}/resolve`, { summary });
      toast.success("Ticket resolved");
      fetch(true);
      setSelected(null);
    } catch { toast.error("Failed to resolve"); }
  };

  const handleRetriage = async (id) => {
    try {
      await axios.post(`${API}/it-tickets/${id}/retriage`);
      toast.success("Re-triaging ticket...");
      fetch(true);
      setSelected(null);
    } catch { toast.error("Failed to re-triage"); }
  };

  const openTicket = async (t) => {
    try {
      const res = await axios.get(`${API}/it-tickets/${t.id}`);
      setSelected(res.data);
    } catch { setSelected(t); }
  };

  const filtered = tickets.filter(t => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterPriority !== "all" && t.priority !== filterPriority) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-6" data-testid="it-tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <Ticket className="w-8 h-8 text-blue-400" /> IT Helpdesk
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Central helpdesk — tickets from all connected apps, AI triages, drafts replies and resolves automatically
          </p>
        </div>
        <Button variant="outline" onClick={() => fetch()}
          className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
          data-testid="refresh-tickets-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/5">
        {[["tickets", "Tickets", Ticket], ["analytics", "Analytics", BarChart3], ["systems", "Connected Systems", Server]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === key ? "border-blue-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
            data-testid={`tab-${key}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "systems" ? <ConnectedSystems /> : tab === "analytics" ? <HelpdeskAnalytics /> : (<>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {Object.entries(STATUS_CFG).map(([status, cfg]) => {
          const count = stats[status] || 0;
          const Icon = cfg.icon;
          return (
            <button key={status} onClick={() => setFilterStatus(status === filterStatus ? "all" : status)}
              className={`p-3 rounded-lg border text-center transition-all ${cfg.color} ${filterStatus === status ? "ring-1 ring-white/20" : ""}`}
              data-testid={`stat-${status}`}>
              <p className="text-xl font-bold font-mono">{count}</p>
              <p className="text-[10px] opacity-70 mt-0.5">{cfg.label}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-36 bg-black/50 border-white/10 text-white text-xs h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            <SelectItem value="all">All Priorities</SelectItem>
            {["critical","high","medium","low"].map(p =>
              <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-500">{filtered.length} tickets</span>
      </div>

      {/* Ticket List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-16 text-center space-y-3">
            <Ticket className="w-14 h-14 text-zinc-700 mx-auto" />
            <p className="text-lg font-medium text-white">No tickets yet</p>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">
              Tickets submitted from any connected app (see the Connected Systems tab) will appear here, get AI-triaged, and a draft reply prepared for your review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(ticket => {
            const cfg = STATUS_CFG[ticket.status] || STATUS_CFG.open;
            const StatusIcon = cfg.icon;
            const isLive = ["triaging","in_progress"].includes(ticket.status);
            return (
              <div key={ticket.id}
                onClick={() => openTicket(ticket)}
                className="flex items-center gap-4 p-3 bg-[#0c0c0e] border border-white/5 hover:border-white/10 rounded-lg cursor-pointer transition-all"
                data-testid={`ticket-row-${ticket.id}`}>
                {/* Ticket number */}
                <span className="text-xs font-mono text-zinc-500 w-16 shrink-0">{ticket.ticket_number}</span>
                {/* Status */}
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border whitespace-nowrap ${cfg.color}`}>
                  <StatusIcon className={`w-3 h-3 ${isLive ? "animate-spin" : ""}`} />
                  {cfg.label}
                </span>
                {/* Priority */}
                <span className={`px-2 py-0.5 rounded-full text-[11px] border whitespace-nowrap ${PRIORITY_CFG[ticket.priority] || PRIORITY_CFG.medium}`}>
                  {ticket.priority}
                </span>
                {/* Category */}
                <span className="text-xs text-zinc-500 shrink-0">{CATEGORY_ICONS[ticket.category]} {ticket.category}</span>
                {/* Description */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{ticket.triage_notes || ticket.description}</p>
                  <p className="text-xs text-zinc-500 truncate">
                    {ticket.name} · {ticket.department}{ticket.system_name ? ` · ${ticket.system_name}` : ""}
                  </p>
                </div>
                {/* Reply pending indicator */}
                {ticket.reply_status === "draft" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 shrink-0 whitespace-nowrap">
                    reply to review
                  </span>
                )}
                {/* Time */}
                <span className="text-xs text-zinc-600 shrink-0 hidden sm:block">
                  {new Date(ticket.created_at).toLocaleDateString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
      </>)}

      {/* Ticket detail modal */}
      {selected && (
        <TicketModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onResolve={handleResolve}
          onRetriage={handleRetriage}
        />
      )}
    </div>
  );
}
